import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Clock, AlertCircle, Sparkles, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTrialStatus, useTrialAlertMessage, useTrialAlertColor } from '../../hooks/useTrialStatus'
import { useAuth } from '../../contexts/AuthContext'
import { useState } from 'react'

interface TrialCountdownBannerProps {
  className?: string
}

/**
 * Banner fixo que mostra o countdown do trial
 * Aparece quando faltam 7, 3 ou 1 dia
 * Pode ser dismissível mas reaparece na próxima sessão
 */
const TrialCountdownBanner: React.FC<TrialCountdownBannerProps> = ({ className = '' }) => {
  const navigate = useNavigate()
  const { isInTrial, daysLeft, isExpiringSoon, isExpiringToday, trialProgress } = useTrialStatus()
  const alertMessage = useTrialAlertMessage()
  const alertColor = useTrialAlertColor()
  const { isAdmin } = useAuth()
  const [isDismissed, setIsDismissed] = useState(false)

  // Não mostrar se não está em trial, foi dismissado, é admin,
  // ou (defensivo) não há mensagem a exibir
  if (!isInTrial || isDismissed || isAdmin || !alertMessage) {
    return null
  }

  // Cores baseadas na urgência
  const colorClasses = {
    green: {
      bg: 'bg-moss-100 dark:bg-moss-500/20 dark:bg-moss-500/20',
      border: 'border-moss-100 dark:border-moss-500',
      text: 'text-moss-500 dark:text-moss-500',
      icon: 'text-moss-500 dark:text-moss-500',
      button: 'bg-moss-500 hover:bg-moss-500/90',
      progress: 'bg-moss-500'
    },
    yellow: {
      bg: 'bg-ochre-100 dark:bg-ochre-500/20 dark:bg-ochre-500/20',
      border: 'border-ochre-100 dark:border-ochre-500',
      text: 'text-ochre-500 dark:text-ochre-500',
      icon: 'text-ochre-500 dark:text-ochre-500',
      button: 'bg-ochre-500 hover:bg-ochre-500/90',
      progress: 'bg-ochre-500'
    },
    orange: {
      bg: 'bg-ochre-100 dark:bg-ochre-500/20',
      border: 'border-ochre-100 dark:border-ochre-500',
      text: 'text-ochre-500 dark:text-ochre-500',
      icon: 'text-ochre-500 dark:text-ochre-500',
      button: 'bg-ochre-500 hover:bg-ochre-500/90',
      progress: 'bg-ochre-500'
    },
    red: {
      bg: 'bg-blood-100 dark:bg-blood-500/20 dark:bg-blood-500/20',
      border: 'border-blood-100 dark:border-blood-500',
      text: 'text-blood-500 dark:text-blood-500',
      icon: 'text-blood-500 dark:text-blood-500',
      button: 'bg-blood-500 hover:bg-blood-500/90',
      progress: 'bg-blood-500'
    }
  }

  const colors = colorClasses[alertColor]

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`relative border ${colors.border} ${colors.bg} ${className}`}
      >
        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-cream-200 dark:bg-ink-700">
          <motion.div
            className={`h-full ${colors.progress}`}
            initial={{ width: 0 }}
            animate={{ width: `${trialProgress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Icon + Message */}
            <div className="flex items-center gap-3 flex-1">
              {isExpiringToday ? (
                <AlertCircle className={`w-5 h-5 ${colors.icon} flex-shrink-0 animate-pulse`} />
              ) : isExpiringSoon ? (
                <Clock className={`w-5 h-5 ${colors.icon} flex-shrink-0`} />
              ) : (
                <Sparkles className={`w-5 h-5 ${colors.icon} flex-shrink-0`} />
              )}

              <div className="flex-1">
                <p className={`text-sm font-medium ${colors.text}`}>
                  {alertMessage}
                </p>
              </div>
            </div>

            {/* Right: CTA Button + Dismiss */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/app/billing')}
                className={`${colors.button} text-white px-4 py-1.5 rounded-caderno-sm text-sm font-medium transition-colors whitespace-nowrap`}
              >
                {isExpiringToday ? 'Assinar Agora' : 'Ver Planos'}
              </button>

              {!isExpiringToday && (
                <button
                  onClick={() => setIsDismissed(true)}
                  className={`p-1.5 ${colors.text} hover:bg-black/5 dark:hover:bg-white/5 rounded-caderno-xs transition-colors`}
                  aria-label="Dispensar"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default TrialCountdownBanner
